import { Button, Grid, Link, List, Pill, Spinner, Text } from "@instructure/ui";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "../../../../../components/Card";
import {
  ERROR,
  SUCCESS,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import {
  FAIL,
  GOOD,
  useStatus,
  WORKING,
} from "../../../../../components/CustomHooks/StatusProvider";
import { AssignmentService } from "../../../../../services/AssignmentService";
import { TaskService } from "../../../../../services/TaskService";
import { TermsContext } from "../../../../../utils/Context";
import { sleep, waitForExpectedResult } from "../../../../../utils/Timer";
import {
  dueDateAfterTerm,
  getDueDate,
} from "../../Components/SectionCard/utils";

const MAX_RETRIES = 24;
const RETRY_DELAY_MS = 5000;

const SectionsByTermCard = ({
  sections,
  term,
  courseCode,
  finalizingAll,
  defaultDeadline,
  setCoordinatedSections,
}) => {
  const { t, i18n } = useTranslation();
  const { insightsTerms } = useContext(TermsContext);
  const showNotification = useNotification();
  const { updateStatus, toggleStatus } = useStatus();
  const [finalizing, setFinalizing] = useState(false);
  const dueDate = dueDateAfterTerm(sections[0], defaultDeadline);

  const finalizationDeadline = getDueDate(
    sections[0].term,
    dueDate,
    insightsTerms
  );

  const formattedDueDate = new Intl.DateTimeFormat(i18n.language, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(finalizationDeadline);

  async function handleVerification(sectionCanvasId, taskId, sharedState) {
    let attempts = 0;
    while (attempts < MAX_RETRIES) {
      if (sharedState.taskCompleted) {
        return true; // short-circuit
      }
      attempts++;

      const isFinalized = await AssignmentService.getFinalizedResultsExist(
        sectionCanvasId,
        taskId
      );

      if (isFinalized) {
        return true;
      }
      await sleep(RETRY_DELAY_MS);
    }
    return false;
  }

  async function processSection(canvas_id, taskId, sharedState) {
    try {
      const results = await AssignmentService.getResultsByContext(
        AssignmentService.ROLLUP,
        canvas_id
      );

      await AssignmentService.finalizeResultsByContext({
        mode: AssignmentService.ROLLUP,
        roundingMode: "roundDown",
        contextId: canvas_id,
        data: JSON.stringify(results.data),
        bulkTaskId: taskId,
      });

      const verified = await handleVerification(canvas_id, taskId, sharedState);

      if (!verified) {
        sharedState.verificationFailures++;

        // If ALL sections failed verification
        if (
          sharedState.verificationFailures === sharedState.total &&
          !sharedState.taskCompleted
        ) {
          console.debug(
            "All verifications exhausted retries before task completed."
          );
        }
      }
    } catch (err) {
      console.debug("Section failed:", canvas_id, err);
    }
  }

  function processBulkFinalizeTask(task, finalizedCanvasIds) {
    if (task.status === "done") {
      showNotification(t("notifications.results.bulkFinalizeSuccess"), SUCCESS);
      setCoordinatedSections((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([t, secs]) => {
          const remaining = secs.filter(
            (s) => !finalizedCanvasIds.includes(s.canvas_id)
          );
          if (remaining.length > 0) {
            updated[t] = remaining;
          }
        });
        return updated;
      });
    } else if (task.status === "error") {
      showNotification(t("notifications.results.bulkFinalizeFailed"), ERROR);
      updateStatus({
        service: { name: t("status.bulkFinalizeStatus"), status: FAIL },
      });
    }
  }

  function pollTaskUntilDone(taskId, sharedState, finalizedCanvasIds) {
    showNotification(
      <Text>
        {t("notifications.results.bulkFinalize")}
        <Link
          themeOverride={{ fontWeight: "bold" }}
          onClick={() => toggleStatus(true)}
        >
          {t("status.checkStatus")}
        </Link>
      </Text>,
      SUCCESS,
      10000
    );

    updateStatus({
      service: {
        name: t("status.bulkFinalizeStatus"),
        status: WORKING,
        progress: 0,
      },
    });

    return new Promise((resolve) => {
      waitForExpectedResult(
        () => TaskService.getTaskById(taskId),

        (task) => {
          const total = task.obj?.canvasCourseIds?.length || 1;
          const progress = Math.min(
            100,
            parseInt((task.counter / total) * 100)
          );
          updateStatus({
            service: {
              name: t("status.bulkFinalizeStatus"),
              status: progress === 100 ? GOOD : WORKING,
              progress,
            },
          });
          const done = task.status === "done" || task.status === "error";
          if (done) {
            sharedState.taskCompleted = true;
          }
          return done;
        },

        (task) => {
          sharedState.taskCompleted = true;
          processBulkFinalizeTask(task, finalizedCanvasIds);
          resolve(task);
        },

        () => {
          showNotification(
            t("notifications.results.bulkFinalizeFailed"),
            ERROR
          );

          updateStatus({
            service: { name: t("status.bulkFinalizeStatus"), status: FAIL },
          });

          resolve(null);
        },

        500,
        5000
      );
    });
  }

  async function handleSubmit(sections) {
    try {
      const courseCanvasIds = sections.map((s) => s.canvas_id);
      if (courseCanvasIds.length === 0) {
        showNotification(t("analyze.bulkFinalizeError"), ERROR);
        return;
      }
      setFinalizing(true);

      const taskPayload = {
        title: "bulk-finalize-results-task",
        obj: { canvasCourseIds: courseCanvasIds },
      };
      const createdTask = await TaskService.createTask(taskPayload);

      const sharedState = {
        taskCompleted: false,
        verificationFailures: 0,
        total: courseCanvasIds.length,
      };

      const taskPromise = pollTaskUntilDone(
        createdTask.ddl_id,
        sharedState,
        courseCanvasIds
      );

      const verificationPromise = Promise.all(
        courseCanvasIds.map((canvas_id) =>
          processSection(canvas_id, createdTask.ddl_id, sharedState)
        )
      );

      await Promise.race([taskPromise, verificationPromise]);
    } catch {
      showNotification(t("analyze.bulkFinalizeError"), ERROR);
    } finally {
      setFinalizing(false);
    }
  }

  return (
    finalizationDeadline < new Date() && (
      <Card style={{ backgroundColor: "#FFFFFF" }}>
        <Grid startAt="medium">
          <Grid.Row>
            <Grid.Col width={8}>
              <b>{term}:</b>
              <List isUnstyled itemSpacing="x-small">
                {sections.map((section, index) => (
                  <List.Item key={index}>
                    <Text>
                      {section.name}:{" "}
                      {section.finalized ? (
                        <Pill color="success">
                          {t("analyze.finalizedResults")}
                        </Pill>
                      ) : (
                        <Pill color="warning">
                          {t("analyze.unfinalizedResults")}
                        </Pill>
                      )}
                    </Text>
                  </List.Item>
                ))}
              </List>
            </Grid.Col>
            <Grid.Col width={4} textAlign="end">
              <Button
                id={`finalize-results-button-${courseCode}-${term}`}
                size="small"
                onClick={() => handleSubmit(sections)}
                disabled={
                  sections.every((section) => section.finalized) ||
                  finalizing ||
                  finalizingAll
                }
                margin="0 0 xx-small 0"
              >
                {finalizing && (
                  <Spinner
                    renderTitle={t("global.pleaseWait")}
                    size="x-small"
                    margin="0 xx-small xxx-small 0"
                  />
                )}
                {t("analyze.autoFinalizeResults")}
              </Button>
              <Text color="secondary" fontStyle="italic" as="div">
                {t("resultsDeadline.resultsDue")} {formattedDueDate}
              </Text>
            </Grid.Col>
          </Grid.Row>
        </Grid>
      </Card>
    )
  );
};

export default SectionsByTermCard;
